import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Table, TableHead, TableBody, TableRow, TableCell, Select, TextField } from "@mui/material";
import { colors } from "brand";
import { useTranslation } from "i18n-fe";
import { useNavRailState } from "common-fe";
import { useRingData } from "../context/RingDataContext";
import useRidesForShow from "../hooks/useRidesForShow";
import useRideStatuses from "../hooks/useRideStatuses";
import { useEntityTick } from "../hooks/useEntityTick";
import { STATUS_POLL_MS } from "../constants/polling";
import {
  buildWholeShowRows,
  sortWholeShowRows,
  filterWholeShowRows,
  distinctFilterOptions,
  SORT_COLUMNS,
  DEFAULT_FILTERS,
} from "../utils/wholeShowList";
import { formatTime } from "../utils/formatTime";
import { formatComposite } from "../utils/formatComposite";

// #2141 (docs/score/announcer-ui-redesign.md §5) — the whole-show list: its
// own view (AC1), never mounted alongside the four /oog bands. Pure
// row-build/sort/filter logic lives in utils/wholeShowList.js (mutation-
// proved there already); this file is the DOM/interaction surface.
//
// Data: useRidesForShow (showId-only GET /ride, AC8) + a LOCAL
// useRideStatuses batch poll for the server-derived status overlay — both
// deliberately kept OUT of RingDataContext (see each hook's own header
// comment) so ring-data-poll-set.cy.js's pinned per-hook counts, measured
// with /oog mounted and /list NOT mounted, never move because of this view.

// Same trap AnnouncerFrame.jsx's own header comment documents (#2138):
// common-fe's MainContent only ever constrains `minHeight` and sets
// `overflow: "auto"` on ITSELF, so normal-flow content taller than the
// viewport makes MainContent scroll, not a nested `height: "100%"` box —
// `height: 100%` against an `auto`-height ancestor is itself `auto`. AC5/AC6
// need a REAL bounded scroller (`whole-show-scroll` below), so this view
// pins itself to the viewport exactly like AnnouncerFrame does, instead of
// trying to fill a MainContent that never hands out a real height.
const APP_BAR_HEIGHT = 64;
const APP_FOOTER_HEIGHT = 56;

const DASH = "—";

const COLUMN_LABEL_KEYS = {
  number: "wholeShowList.columns.number",
  horse: "wholeShowList.columns.horse",
  rider: "wholeShowList.columns.rider",
  className: "wholeShowList.columns.className",
  ring: "wholeShowList.columns.ring",
  scheduledTime: "wholeShowList.columns.scheduledTime",
  score: "wholeShowList.columns.score",
  placing: "wholeShowList.columns.placing",
  status: "wholeShowList.columns.status",
};

// `score` is a dashed placeholder by design (see wholeShowList.js header +
// card #2141 comment thread): computing it client-side would mean
// reimplementing score-api's exposeScores withhold-until-confirmed gate,
// which its own header comment says is deliberately never duplicated.
function cellValue(row, column) {
  switch (column) {
    case "number":
      return row.number || DASH;
    case "horse":
      return row.horse || DASH;
    case "rider":
      return row.rider || DASH;
    case "className":
      return `${row.classNumber || ""} ${row.className || ""}`.trim() || DASH;
    case "ring":
      return row.ring || DASH;
    case "scheduledTime":
      return formatTime(row.scheduledTime) || DASH;
    case "score":
      return formatComposite(row.score) || DASH;
    case "placing":
      return row.placing ? String(row.placing) : DASH;
    case "status":
      return (row.status || "").toLowerCase();
    default:
      return DASH;
  }
}

function FilterSelect({ testId, value, onChange, options, allLabel }) {
  return (
    <Select
      native
      size="small"
      inputProps={{ "data-testid": testId }}
      value={value || ""}
      onChange={(e) => onChange(e.target.value || null)}
    >
      <option value="">{allLabel}</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </Select>
  );
}

export default function WholeShowList() {
  const { t } = useTranslation("announcer");
  const navigate = useNavigate();
  const { width: navRailWidth } = useNavRailState();
  const { classes, selectedRing, selectedShow, todayKey, online } = useRingData();
  const showId = selectedShow?.id;

  // This view's own entity beat — deliberately NOT RingDataContext's, since
  // this component is never mounted alongside it (see useRidesForShow.js).
  const localTick = useEntityTick(true);
  const { data: rawRides } = useRidesForShow(showId, localTick);

  const rideIds = useMemo(() => (rawRides || []).map((r) => r.id), [rawRides]);
  const { statuses } = useRideStatuses(rideIds, {
    intervalMs: STATUS_POLL_MS,
    enabled: online && rideIds.length > 0,
  });

  const [sortState, setSortState] = useState({ column: "scheduledTime", direction: "asc" });
  // Seeded with today's venue day on FIRST paint (not a flash-then-narrow
  // useEffect) — redesign doc §5: "opens filtered to today".
  const [filters, setFilters] = useState(() => ({ ...DEFAULT_FILTERS, day: todayKey }));

  const rows = useMemo(() => buildWholeShowRows({ rides: rawRides, classes, statuses }), [rawRides, classes, statuses]);
  const options = useMemo(() => distinctFilterOptions(rows), [rows]);
  const filtered = useMemo(() => filterWholeShowRows(rows, filters), [rows, filters]);
  const sorted = useMemo(
    () => sortWholeShowRows(filtered, sortState.column, sortState.direction),
    [filtered, sortState],
  );

  // AC5 (never self-scrolls) + AC6 (a new sort/filter starts at the top):
  // this effect's deps are ONLY [sortState, filters] — NEVER rows/rawRides/
  // statuses — so a background poll re-rendering the table can never move
  // scroll. That is what makes AC5 true by construction.
  const scrollRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [sortState, filters]);

  const toggleSort = (column) => {
    setSortState((prev) =>
      prev.column === column
        ? { column, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { column, direction: "asc" },
    );
  };

  const setFilter = (name, value) => setFilters((prev) => ({ ...prev, [name]: value }));

  const allLabel = t("wholeShowList.filters.all");

  return (
    <Box
      data-testid="whole-show-list-view"
      sx={{
        position: "fixed",
        top: APP_BAR_HEIGHT,
        left: navRailWidth,
        right: 0,
        bottom: APP_FOOTER_HEIGHT,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "background.default",
        transition: "left 0.15s ease",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", px: 1.5, py: 1, flexShrink: 0 }}>
        <Typography
          data-testid="whole-show-return-button"
          component="button"
          type="button"
          onClick={() => navigate("/oog")}
          variant="body2"
          sx={{
            cursor: "pointer",
            color: colors.foxOrange,
            background: "none",
            border: "none",
            p: 0,
            textAlign: "left",
            font: "inherit",
            fontWeight: 700,
          }}
        >
          {selectedRing
            ? t("wholeShowList.backToRing", { ring: selectedRing })
            : t("wholeShowList.backToRingUnresolved")}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, px: 1.5, pb: 1, flexShrink: 0 }}>
        <FilterSelect
          testId="whole-show-filter-day"
          value={filters.day}
          onChange={(v) => setFilter("day", v)}
          options={options.days}
          allLabel={allLabel}
        />
        <FilterSelect
          testId="whole-show-filter-ring"
          value={filters.ring}
          onChange={(v) => setFilter("ring", v)}
          options={options.rings}
          allLabel={allLabel}
        />
        <Select
          native
          size="small"
          inputProps={{ "data-testid": "whole-show-filter-classId" }}
          value={filters.classId || ""}
          onChange={(e) => setFilter("classId", e.target.value || null)}
        >
          <option value="">{allLabel}</option>
          {options.classes.map((c) => (
            <option key={c.classId} value={c.classId}>
              {`${c.classNumber || ""} ${c.className || ""}`.trim()}
            </option>
          ))}
        </Select>
        <FilterSelect
          testId="whole-show-filter-status"
          value={filters.status}
          onChange={(v) => setFilter("status", v)}
          options={options.statuses}
          allLabel={allLabel}
        />
        <FilterSelect
          testId="whole-show-filter-division"
          value={filters.division}
          onChange={(v) => setFilter("division", v)}
          options={options.divisions}
          allLabel={allLabel}
        />
        <TextField
          size="small"
          placeholder={t("wholeShowList.filters.textPlaceholder")}
          value={filters.text || ""}
          onChange={(e) => setFilter("text", e.target.value)}
          slotProps={{ htmlInput: { "data-testid": "whole-show-filter-text" } }}
        />
      </Box>

      <Box ref={scrollRef} data-testid="whole-show-scroll" sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {sorted.length === 0 ? (
          <Box data-testid="whole-show-empty" sx={{ p: 3, textAlign: "center" }}>
            <Typography color="text.secondary">{t("wholeShowList.empty")}</Typography>
          </Box>
        ) : (
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {SORT_COLUMNS.map((column) => (
                  <TableCell
                    key={column}
                    data-testid={`whole-show-header-${column}`}
                    data-sort={sortState.column === column ? sortState.direction : "none"}
                    onClick={() => toggleSort(column)}
                    sx={{ cursor: "pointer", fontWeight: 700 }}
                  >
                    {t(COLUMN_LABEL_KEYS[column])}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sorted.map((row) => (
                <TableRow key={row.id} data-testid="whole-show-row" data-ride-id={row.id}>
                  {SORT_COLUMNS.map((column) => (
                    <TableCell key={column} data-testid={`whole-show-cell-${column}`}>
                      {cellValue(row, column)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    </Box>
  );
}
